import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import {
  AttendanceAction,
  AttendanceDayStatus,
  AttendanceReport,
  AttendanceReportRow,
  AttendanceService,
  AttendanceStatus,
} from 'src/app/services/attendance.service';
import { ClassesService } from 'src/app/services/classes.service';
import { LanguageService } from 'src/app/services/language.service';
import { SearchableSelectOption } from 'src/app/shared/searchable-select/searchable-select.component';

@Component({
  selector: 'app-manual-attendance',
  templateUrl: './manual-attendance.component.html',
  styleUrls: ['./manual-attendance.component.css'],
})
export class ManualAttendanceComponent implements OnInit {
  selectedClassId = '';
  classes: any[] = [];
  loading = true;
  applying = false;
  report: AttendanceReport | null = null;
  errorMessage = '';
  search = '';
  busyStudentId = '';
  /** Local draft only — not saved until Apply. */
  private pendingPresent = new Set<string>();

  constructor(
    private attendanceService: AttendanceService,
    private classesService: ClassesService,
    private languageService: LanguageService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    void this.init();
  }

  get todayLabel(): string {
    return this.formatDayLong(this.report?.date || this.todayInputValue());
  }

  get lateEnabled(): boolean {
    return this.report?.schedule?.lateEnabled !== false;
  }

  get searchPlaceholder(): string {
    return this.translate.instant('COMMON.SEARCH');
  }

  get selectClassLabel(): string {
    return this.translate.instant('PICKUP.SELECT_CLASS');
  }

  get classOptions(): SearchableSelectOption[] {
    return this.classes.map(cls => ({
      value: String(cls.id || cls._id || ''),
      label: cls.major?.name ? `${cls.name} — ${cls.major.name}` : (cls.name || ''),
      searchText: `${cls.name || ''} ${cls.major?.name || ''}`,
    })).filter(option => option.value);
  }

  get filteredRows(): AttendanceReportRow[] {
    const rows = this.report?.rows || [];
    const q = this.search.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(row => [
      row.studentNameAr,
      row.studentNameEn,
      row.studentCode,
      row.className,
    ].join(' ').toLowerCase().includes(q));
  }

  get pendingCount(): number {
    return this.pendingPresent.size;
  }

  get hasPending(): boolean {
    return this.pendingPresent.size > 0;
  }

  get markableCount(): number {
    return (this.report?.rows || []).filter(row => !row.checkIn?.at).length;
  }

  studentName(row: AttendanceReportRow): string {
    const isAr = this.languageService.current === 'ar';
    return (isAr ? row.studentNameAr : row.studentNameEn) || row.studentNameAr || row.studentNameEn || '—';
  }

  isPending(row: AttendanceReportRow): boolean {
    return this.pendingPresent.has(row.studentId);
  }

  displayStatus(row: AttendanceReportRow): AttendanceDayStatus {
    if (row.checkIn?.at) {
      return row.dayStatus;
    }
    return this.isPending(row) ? 'checked_in' : 'absent';
  }

  dayStatusKey(status: AttendanceDayStatus): string {
    return `ATTENDANCE_DASH.DAY_${status.toUpperCase()}`;
  }

  timingKey(status?: AttendanceStatus | null): string {
    const normalized = (!this.lateEnabled && status === 'late') ? 'on_time' : (status || 'on_time');
    return `ATTENDANCE.STATUS_${normalized.toUpperCase()}`;
  }

  timingStatus(status?: AttendanceStatus | null): AttendanceStatus {
    if (!this.lateEnabled && status === 'late') {
      return 'on_time';
    }
    return status || 'on_time';
  }

  formatTime(at?: number | null): string {
    if (!at) {
      return '—';
    }
    return new Date(at).toLocaleTimeString(this.languageService.current === 'ar' ? 'ar-AE' : 'en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  canCheckIn(row: AttendanceReportRow): boolean {
    return !row.checkIn?.at && !this.isPending(row);
  }

  canCheckOut(row: AttendanceReportRow): boolean {
    return !!row.checkIn?.at && !row.checkOut?.at;
  }

  canMarkAbsentDraft(row: AttendanceReportRow): boolean {
    return !row.checkIn?.at && this.isPending(row);
  }

  canMarkPresentDraft(row: AttendanceReportRow): boolean {
    return !row.checkIn?.at && !this.isPending(row);
  }

  isBusy(row: AttendanceReportRow): boolean {
    return this.busyStudentId === row.studentId || this.applying;
  }

  markAllPresentDraft(): void {
    const rows = this.report?.rows || [];
    for (const row of rows) {
      if (!row.checkIn?.at) {
        this.pendingPresent.add(row.studentId);
      }
    }
    // Trigger change detection for Set mutations
    this.pendingPresent = new Set(this.pendingPresent);
  }

  markPresentDraft(row: AttendanceReportRow): void {
    if (!this.canMarkPresentDraft(row)) {
      return;
    }
    this.pendingPresent.add(row.studentId);
    this.pendingPresent = new Set(this.pendingPresent);
  }

  markAbsentDraft(row: AttendanceReportRow): void {
    if (!this.canMarkAbsentDraft(row)) {
      return;
    }
    this.pendingPresent.delete(row.studentId);
    this.pendingPresent = new Set(this.pendingPresent);
  }

  clearDraft(): void {
    this.pendingPresent = new Set();
  }

  async onClassChange(): Promise<void> {
    this.clearDraft();
    await this.loadReport();
  }

  async loadReport(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    this.clearDraft();

    if (!this.selectedClassId) {
      this.report = null;
      this.loading = false;
      return;
    }

    try {
      this.report = await this.attendanceService.getReport(
        this.todayInputValue(),
        this.selectedClassId,
      );
    } catch {
      this.errorMessage = 'ATTENDANCE_MANUAL.LOAD_FAILED';
      this.report = null;
    } finally {
      this.loading = false;
    }
  }

  async applyDraft(): Promise<void> {
    if (!this.hasPending || this.applying) {
      return;
    }

    const ids = Array.from(this.pendingPresent);
    this.applying = true;
    this.errorMessage = '';
    let saved = 0;
    let failed = 0;

    try {
      for (const studentId of ids) {
        const row = this.report?.rows.find(item => item.studentId === studentId);
        if (!row || row.checkIn?.at) {
          this.pendingPresent.delete(studentId);
          continue;
        }
        this.busyStudentId = studentId;
        try {
          const result = await this.attendanceService.manualMark(studentId, 'check_in');
          this.applyResult(row, result.attendance?.checkIn || null, result.attendance?.checkOut || null);
          this.pendingPresent.delete(studentId);
          if (!result.alreadyScanned) {
            saved += 1;
          } else {
            saved += 1;
          }
        } catch {
          failed += 1;
        }
      }
      this.pendingPresent = new Set(this.pendingPresent);

      if (failed && saved) {
        this.snackBar.open(
          this.translate.instant('ATTENDANCE_MANUAL.APPLY_PARTIAL', { saved, failed }),
          undefined,
          { duration: 4000 },
        );
      } else if (failed) {
        this.snackBar.open(this.translate.instant('ATTENDANCE_MANUAL.APPLY_FAILED'), undefined, { duration: 3500 });
      } else {
        this.snackBar.open(
          this.translate.instant('ATTENDANCE_MANUAL.APPLY_OK', { count: saved }),
          undefined,
          { duration: 2500 },
        );
      }
    } finally {
      this.busyStudentId = '';
      this.applying = false;
    }
  }

  async mark(row: AttendanceReportRow, action: AttendanceAction): Promise<void> {
    if (this.isBusy(row)) {
      return;
    }
    if (action === 'check_in' && !this.canCheckIn(row)) {
      return;
    }
    if (action === 'check_out' && !this.canCheckOut(row)) {
      return;
    }

    this.busyStudentId = row.studentId;
    this.errorMessage = '';
    try {
      const result = await this.attendanceService.manualMark(row.studentId, action);
      this.applyResult(row, result.attendance?.checkIn || null, result.attendance?.checkOut || null);
      this.pendingPresent.delete(row.studentId);
      this.pendingPresent = new Set(this.pendingPresent);

      if (result.alreadyScanned) {
        const key = result.reason === 'already_checked_out'
          ? 'ATTENDANCE.ALREADY_CHECKED_OUT'
          : 'ATTENDANCE.ALREADY_CHECKED_IN';
        this.snackBar.open(this.translate.instant(key), undefined, { duration: 2500 });
      } else {
        const key = action === 'check_out'
          ? 'ATTENDANCE.SCAN_CHECK_OUT_OK'
          : 'ATTENDANCE.SCAN_CHECK_IN_OK';
        this.snackBar.open(this.translate.instant(key), undefined, { duration: 2200 });
      }
    } catch (error: any) {
      const message = error?.error?.errorMessages?.[0]?.message
        || error?.error?.error?.message
        || this.translate.instant('ATTENDANCE_MANUAL.MARK_FAILED');
      this.snackBar.open(String(message), undefined, { duration: 3500 });
    } finally {
      this.busyStudentId = '';
    }
  }

  private applyResult(
    row: AttendanceReportRow,
    checkIn: AttendanceReportRow['checkIn'],
    checkOut: AttendanceReportRow['checkOut'],
  ): void {
    row.checkIn = checkIn;
    row.checkOut = checkOut;
    if (checkIn?.at && checkOut?.at) {
      row.dayStatus = 'complete';
    } else if (checkIn?.at) {
      row.dayStatus = 'checked_in';
    } else {
      row.dayStatus = 'absent';
    }

    if (!this.report) {
      return;
    }
    const summary = {
      total: 0,
      present: 0,
      checked_in: 0,
      complete: 0,
      absent: 0,
      check_in_early: 0,
      check_in_on_time: 0,
      check_in_late: 0,
      check_out_early: 0,
      check_out_on_time: 0,
      check_out_late: 0,
    };
    for (const item of this.report.rows) {
      summary.total += 1;
      if (item.dayStatus === 'absent') {
        summary.absent += 1;
      } else {
        summary.present += 1;
        if (item.dayStatus === 'checked_in') {
          summary.checked_in += 1;
        } else {
          summary.complete += 1;
        }
      }
      if (item.checkIn?.status === 'early') summary.check_in_early += 1;
      if (item.checkIn?.status === 'on_time') summary.check_in_on_time += 1;
      if (item.checkIn?.status === 'late') summary.check_in_late += 1;
      if (item.checkOut?.status === 'early') summary.check_out_early += 1;
      if (item.checkOut?.status === 'on_time') summary.check_out_on_time += 1;
      if (item.checkOut?.status === 'late') summary.check_out_late += 1;
    }
    this.report.summary = summary;
  }

  private async init(): Promise<void> {
    try {
      const classes = await this.classesService.list();
      this.classes = (classes || []).slice().sort((a: any, b: any) =>
        String(a.name || '').localeCompare(String(b.name || ''), 'ar')
      );
      if (!this.selectedClassId && this.classes.length) {
        this.selectedClassId = String(this.classes[0].id || this.classes[0]._id || '');
      }
    } catch {
      this.classes = [];
    }
    await this.loadReport();
  }

  private todayInputValue(): string {
    const now = new Date();
    const dubai = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    const y = dubai.getFullYear();
    const m = String(dubai.getMonth() + 1).padStart(2, '0');
    const d = String(dubai.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private formatDayLong(date: string): string {
    const value = new Date(`${date}T12:00:00`);
    if (Number.isNaN(value.getTime())) {
      return date;
    }
    return value.toLocaleDateString(this.languageService.current === 'ar' ? 'ar-EG' : 'en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
}
